import { useCallback, useEffect, useRef, useState } from 'react';
import { get, post } from '../../../axios/axios';

/** 章节改稿用到的后端接口（PaperEditorController，/ai/paper/editor/v2） */
export const REWRITE_API = {
    submit: '/ai/ai/paper/editor/v2/rewrite',
    status: '/ai/ai/paper/editor/v2/rewrite/status',
    result: '/ai/ai/paper/editor/v2/rewrite/result',
    confirm: '/ai/ai/paper/editor/v2/rewrite/confirm',
};

/** 后端 AiPaperRewriteTask.status */
export const TASK_STATUS = { PENDING: 0, RUNNING: 1, DONE: 2, FAILED: 3 };

/** 任务阶段 */
export const REWRITE_PHASE = {
    IDLE: 'idle',
    SUBMITTING: 'submitting',
    RUNNING: 'running',
    READY: 'ready', // 已拿到结果，等待用户应用 / 放弃
    CONFIRMING: 'confirming',
    FAILED: 'failed',
};

const RUNNING_HINTS = [
    '正在分析章节上下文…',
    '正在检索相关文献…',
    '正在构建论证框架…',
    'AI 正在撰写新内容…',
    '正在校对与润色…',
];

const IDLE_STATE = {
    phase: REWRITE_PHASE.IDLE,
    taskId: null,
    progress: 0,
    statusText: '',
    result: null,
    error: null,
};

const errorMessage = (error, fallback) =>
    error?.response?.data?.msg || error?.message || fallback;

const isCanceled = (error) =>
    error?.name === 'AbortError' ||
    error?.name === 'CanceledError' ||
    error?.code === 'ERR_CANCELED';

/**
 * 章节改稿任务状态机
 *
 * start → POST /rewrite 拿 taskId → 轮询 GET /rewrite/status
 *   → 2 完成：GET /rewrite/result 拿新内容块（READY）
 *   → 3 失败：FAILED
 * confirm → POST /rewrite/confirm 写回后端，返回正式内容块
 * cancel / reset → 停止轮询并回到 IDLE
 */
export function useRewriteTask({ pollInterval = 2000, maxPolls = 180 } = {}) {
    const [state, setState] = useState(IDLE_STATE);
    const runRef = useRef(0); // 每次 start / cancel 递增，用来丢弃过期的异步回调
    const timerRef = useRef(null);
    const taskIdRef = useRef(null);

    const stopTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const reset = useCallback(() => {
        runRef.current += 1;
        stopTimer();
        taskIdRef.current = null;
        setState(IDLE_STATE);
    }, [stopTimer]);

    useEffect(() => {
        return () => {
            runRef.current += 1;
            stopTimer();
        };
    }, [stopTimer]);

    const fail = useCallback((run, error, fallback) => {
        if (run !== runRef.current) return;
        timerRef.current = null;
        setState((prev) => ({
            ...prev,
            phase: REWRITE_PHASE.FAILED,
            statusText: '',
            error: errorMessage(error, fallback),
        }));
    }, []);

    const start = useCallback(
        async ({ detailId, instruction, contentBlockIds, useCnReference, useEnReference }) => {
            runRef.current += 1;
            const run = runRef.current;
            stopTimer();
            taskIdRef.current = null;
            setState({
                ...IDLE_STATE,
                phase: REWRITE_PHASE.SUBMITTING,
                statusText: '正在提交改稿任务…',
            });

            let taskId = null;
            try {
                const payload = {
                    detailId,
                    newPrompt: instruction,
                    useCnReference,
                    useEnReference,
                };
                if (Array.isArray(contentBlockIds) && contentBlockIds.length > 0) {
                    payload.contentBlockIds = contentBlockIds;
                }
                const response = await post(REWRITE_API.submit, payload);
                taskId = response?.data;
                if (!taskId) throw new Error('未获取到改稿任务编号');
            } catch (error) {
                if (isCanceled(error)) return;
                fail(run, error, '提交改稿任务失败');
                return;
            }
            if (run !== runRef.current) return;

            taskIdRef.current = taskId;
            setState((prev) => ({
                ...prev,
                phase: REWRITE_PHASE.RUNNING,
                taskId,
                progress: 12,
                statusText: '任务已提交，AI 正在改写…',
            }));

            let polls = 0;
            const poll = async () => {
                if (run !== runRef.current) return;
                polls += 1;
                try {
                    const statusRes = await get(REWRITE_API.status, { taskId });
                    if (run !== runRef.current) return;
                    const status = statusRes?.data;

                    if (status === TASK_STATUS.DONE) {
                        setState((prev) => ({
                            ...prev,
                            progress: 92,
                            statusText: '正在获取改稿结果…',
                        }));
                        const resultRes = await get(REWRITE_API.result, { taskId });
                        if (run !== runRef.current) return;
                        const data = resultRes?.data || {};
                        const blocks = data.blocks;
                        if (!Array.isArray(blocks) || blocks.length === 0) {
                            throw new Error(data.errorMsg || '改稿结果为空，请重试');
                        }
                        timerRef.current = null;
                        setState((prev) => ({
                            ...prev,
                            phase: REWRITE_PHASE.READY,
                            progress: 100,
                            statusText: '改稿完成',
                            result: { blocks, resultContent: data.resultContent || '' },
                            error: null,
                        }));
                        return;
                    }

                    if (status === TASK_STATUS.FAILED) {
                        throw new Error('改稿任务执行失败，请稍后重试');
                    }

                    if (polls >= maxPolls) {
                        throw new Error('改稿耗时过长，请稍后重试');
                    }

                    setState((prev) => ({
                        ...prev,
                        progress: Math.min(prev.progress + 4, 85),
                        statusText:
                            RUNNING_HINTS[Math.min(Math.floor(polls / 4), RUNNING_HINTS.length - 1)],
                    }));
                    timerRef.current = setTimeout(poll, pollInterval);
                } catch (error) {
                    if (isCanceled(error)) return;
                    fail(run, error, '查询改稿进度失败');
                }
            };

            timerRef.current = setTimeout(poll, pollInterval);
        },
        [fail, maxPolls, pollInterval, stopTimer],
    );

    /** 把改稿结果写回后端；成功时返回正式内容块，失败时返回 null 并回到 READY */
    const confirm = useCallback(async () => {
        const taskId = taskIdRef.current;
        if (!taskId) return null;
        const run = runRef.current;
        setState((prev) => ({ ...prev, phase: REWRITE_PHASE.CONFIRMING, error: null }));
        try {
            const response = await post(`${REWRITE_API.confirm}?taskId=${taskId}`);
            const blocks = response?.data;
            if (!Array.isArray(blocks) || blocks.length === 0) {
                throw new Error('确认改稿失败：后端未返回内容');
            }
            if (run !== runRef.current) return null;
            return blocks;
        } catch (error) {
            if (run !== runRef.current || isCanceled(error)) return null;
            setState((prev) => ({
                ...prev,
                phase: REWRITE_PHASE.READY,
                error: errorMessage(error, '应用改稿失败，请重试'),
            }));
            return null;
        }
    }, []);

    const isBusy =
        state.phase === REWRITE_PHASE.SUBMITTING || state.phase === REWRITE_PHASE.RUNNING;

    return {
        ...state,
        isBusy,
        start,
        confirm,
        cancel: reset,
        reset,
    };
}
